import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useCatalogUrl } from '@bluheadless/ui-logic/src/hooks/useCatalogUrl'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useQuickBuy } from '@bluheadless/ui-logic/src/providers/quick-buy'
import { useComparableProducts } from '@bluheadless/ui-logic/src/hooks/product/useComparableProducts'
import { useProductOptionsForQueryParams } from '@bluheadless/ui-logic/src/hooks/product/useProductOptionsForQueryParams'
import { useTrackingsActions } from '@bluheadless/ui-logic/src/hooks/trackings'
import { useUserUniqueId } from '@bluheadless/ui-logic/src/hooks/useUserUniqueId'
import { useOosNotificationContext } from '@bluheadless/ui-logic/src/providers/oos-notification'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { getAttributesByOptionsSelected } from '@bluheadless/ui-logic/src/utils/product'
import {
	PRODUCT_TYPE_BUNDLE,
	PRODUCT_TYPE_CONFIGURABLE,
	PRODUCT_TYPE_GIFTCARD,
	PRODUCT_TYPE_GROUPED,
} from '@bluheadless/ui-logic/src/constants'
import { useProductPriceProps } from '../../product-price'
import { elementType, object, string } from 'prop-types'
import isFinite from 'lodash/isFinite'
import { useMemo, useState } from 'react'
import { useThemeProps } from '@mui/system'
import { useIntl } from 'react-intl'
import RichContent from '../../../particles/rich-content'
import TileMiddleware from './tile-middleware'
import { useCurrency } from '@bluheadless/ui-logic/src/providers/currency'
import { useUser } from '@bluheadless/ui-logic/src/providers/user'
import { ValidationError } from '@bluheadless/ui-logic/src/errors'
import { usePageContext } from '@bluheadless/ui-logic/src/providers/page/page'

const NEED_QUICK_BUY = [PRODUCT_TYPE_BUNDLE, PRODUCT_TYPE_GROUPED, PRODUCT_TYPE_GIFTCARD, PRODUCT_TYPE_CONFIGURABLE]

const getTitleValue = (attributeCode, name, sku, description, shortDescription, attributeValue) => {
	switch (attributeCode) {
		case 'name':
			return name
		case 'sku':
			return sku
		case 'description':
			return <RichContent content={description} />
		case 'shortDescription':
			return <RichContent content={shortDescription} />
		default:
			return attributeValue
	}
}

const TileBridge = ({
	variant,
	imageProps,
	hoverImageProps,
	videoProps,
	title1Props,
	title2Props,
	wishListProps,
	addToCartProps,
	priceProps,
	additionalLabelsProps,
	colorsProps,
	ratingProps: _ratingProps,
	onUpdateProduct,
	onClick,
	seoBannerId,
	seoBannerName,
	seoBannerCreative,
	seoBannerPosition,
	pageContext,
	pageId: _pageId,
	widgetContext,
	...props
}) => {
	const bhTileBaseDefaultProps = useThemeProps({
		name: 'BHTileBase',
	})

	const { formatMessage } = useIntl()

	const {
		siteName,
		catalog: {
			products: {
				images: { width, height } = {},
				placeholder,
				comparable: { enabled: comparableEnabled },
				imageType: { catalogBase, catalogHover },
				rating: { enabled: ratingEnabled },
				oosNotification: { enabled: enableOosNotifications },
			},
			categories: { altImgSuffixAttributeCode },
			tileAttributesMapping: { title1, title2 },
		},
		projectSpecific: { variantsConfigurableNotQuickBuy: variantsConfigurableNotQuickBuy = [] },
	} = useConfig()

	const variants =
		variantsConfigurableNotQuickBuy.length > 0 ? variantsConfigurableNotQuickBuy : ['advanced', 'list', 'review']

	const [
		{
			product = {},
			loadingWishList,
			loadingAddToCart,
			isInWishList,
			prices,
			assets,
			inventory: { inStock, saleable, allowBackOrders },
			selectedOptionsIdsValues,
			configurableChildrenProducts,
			groupedChildrenProducts,
			bundleProducts,
			selectedProduct: { id: selectedProductId, sku: selectedProductSku, name: selectedProductName } = {},
		},
		{ addToCart, addToWishList, removeFromWishList, validateMinimalSelection },
	] = useProductContext()

	const [, { openOosNotification }] = useOosNotificationContext()
	const { enqueueNotification } = useNotification()

	const [initialProduct] = useState(product)

	const { productClick, promotionClick } = useTrackingsActions()
	const { entityType: pageType, __bhEntityId: pageId } = usePageContext()
	const userUniqueId = useUserUniqueId()
	const [{ isLoggedIn }] = useUser()

	const { configurableOptions = [], bundleOptions = [] } = product

	const productOptionsForQueryParams = useProductOptionsForQueryParams({
		selectedOptionsIdsValues,
		configurableOptions,
		bundleOptions,
	})

	const { addItem: addToCompare, removeItem: removeFromCompare, isInComparison, isComparable } = useComparableProducts()

	const { type, name, sku, urlKey, attributes, description, shortDescription, rating } = product

	const title1AttributeValue = attributes?.[title1]?.value

	const title1Value = useMemo(
		() => getTitleValue(title1, name, sku, description, shortDescription, title1AttributeValue),
		[title1, name, sku, description, shortDescription, title1AttributeValue]
	)

	const title2AttributeValue = attributes?.[title2]?.value

	const title2Value = useMemo(
		() => getTitleValue(title2, name, sku, description, shortDescription, title2AttributeValue),
		[title2, name, sku, description, shortDescription, title2AttributeValue]
	)

	const productUrl = useCatalogUrl(urlKey, productOptionsForQueryParams)
	const getImageAltTag = () => {
		return (
			name +
			(altImgSuffixAttributeCode && attributes?.[altImgSuffixAttributeCode]?.value
				? ' - ' + attributes?.[altImgSuffixAttributeCode]?.value
				: '') +
			' - ' +
			siteName
		)
	}

	const mainAsset = assets.find((asset) =>
		asset.type == 'image' ? asset.roles.includes(catalogBase) && asset.type === 'image' : asset.type === 'video'
	) ?? {
		image: placeholder,
	}

	const [, { openQuickBuy }] = useQuickBuy()

	// First asset without role 'image'
	const [alternateAsset] = assets
		.filter((asset) => asset.roles.includes(catalogHover) && asset.type === 'image')
		.filter((asset) => !asset.roles.includes('image'))

	const productPriceProps = useProductPriceProps({ displayContext: 'tiles', ...priceProps })

	const ratingProps = {
		...rating,
		..._ratingProps,
		enabled: _ratingProps?.enabled ?? ratingEnabled,
	}

	const attributesSelected = useMemo(() => {
		return getAttributesByOptionsSelected(configurableOptions, selectedOptionsIdsValues)
	}, [configurableOptions, selectedOptionsIdsValues])

	const { currency } = useCurrency()

	return (
		<TileMiddleware
			imageProps={{
				...imageProps,
				src: mainAsset?.image,
				alt: mainAsset?.alt || getImageAltTag(),
				originalWidth: width,
				originalHeight: height,
			}}
			hoverImageProps={
				mainAsset.type === 'image'
					? {
							...hoverImageProps,
							src: alternateAsset?.image,
							alt: alternateAsset?.alt || getImageAltTag(),
							originalWidth: width,
							originalHeight: height,
					  }
					: {}
			}
			videoProps={
				mainAsset.type === 'video'
					? {
							...videoProps,
							src: mainAsset?.video,
							alt: mainAsset?.alt || getImageAltTag(),
							originalWidth: width,
							originalHeight: height,
							loop: true,
							controls: false,
					  }
					: null
			}
			title1={title1Value}
			title1Props={title1Props}
			title2={title2Value}
			title2Props={title2Props}
			url={productUrl}
			wishListProps={{
				...bhTileBaseDefaultProps?.wishListProps,
				...wishListProps,
				loading: loadingWishList,
				disabled: loadingWishList,
				onClick: isInWishList ? removeFromWishList : addToWishList,
				added: isInWishList,
			}}
			addToCartProps={
				inStock
					? saleable
						? {
								...bhTileBaseDefaultProps?.addToCartProps,
								...addToCartProps,
								loading: loadingAddToCart,
								disabled: loadingAddToCart,
								onClick:
									!NEED_QUICK_BUY.includes(type) || (type === PRODUCT_TYPE_CONFIGURABLE && variants.includes(variant))
										? async () => {
												try {
													await addToCart(() => {
														throw new ValidationError()
													})
													enqueueNotification({
														uiProps: {
															message: formatMessage({ id: 'add_to_cart_success' }),
															severity: 'success',
														},
													})
												} catch (error) {
													if (error instanceof ValidationError) {
														return
													}
													console.error(error?.response?.data?.error?.message ?? error)
													enqueueNotification({
														uiProps: {
															message:
																error?.response?.data?.error?.message || formatMessage({ id: 'add_to_cart_error' }),
															severity: 'error',
														},
													})
												}
										  }
										: () =>
												openQuickBuy({
													product,
													pageContext: pageContext ?? pageType,
													pageId: _pageId ?? pageId,
													widgetContext,
												}),
								...(allowBackOrders ? { label: formatMessage({ id: 'action_add_to_cart_backorder' }) } : {}),
						  }
						: null
					: null
			}
			comparableProps={
				comparableEnabled
					? {
							disabled: !isComparable && !isInComparison({ id: product.id }),
							onChange: isInComparison({ id: product.id })
								? () => removeFromCompare({ id: product.id })
								: () => addToCompare({ id: product.id }),
							added: isInComparison({ id: product.id }),
					  }
					: null
			}
			additionalLabelsProps={{ ...additionalLabelsProps }}
			priceProps={
				type === PRODUCT_TYPE_GROUPED || productPriceProps.hide || priceProps?.hide
					? null
					: isFinite(prices.base)
					? productPriceProps
					: null
			}
			rating={ratingProps}
			colorsProps={{ ...colorsProps }}
			description={description}
			shortDescription={shortDescription}
			type={type}
			variant={variant}
			onClick={(e) => {
				productClick({
					params: {
						product,
						position: props.position,
						userUniqueId,
						currency,
						groupedChildrenProducts,
						configurableChildrenProducts,
						bundleProducts,
						isLoggedIn,
					},
					pageContext: pageContext ?? pageType,
					pageId: _pageId ?? pageId,
					widgetContext,
				})
				if (seoBannerId && seoBannerName && seoBannerCreative && seoBannerPosition) {
					promotionClick({
						event: e,
						id: seoBannerId,
						name: seoBannerName,
						creative: seoBannerCreative,
						position: seoBannerPosition,
					})
				}
				if (onClick) {
					onClick(e, { product })
				}
			}}
			initialProduct={initialProduct}
			configurableChildrenProducts={configurableChildrenProducts}
			onUpdateProduct={onUpdateProduct}
			outOfStockProps={{
				enabled: enableOosNotifications,
				inStock,
				productType: type,
				fullWidth: true,
				onClick: async () => {
					if (await validateMinimalSelection()) {
						openOosNotification({
							productId: selectedProductId,
							productSku: selectedProductSku,
							title1: selectedProductName,
							attributes: attributesSelected,
							priceProps: productPriceProps,
							image: {
								alt: mainAsset?.alt || selectedProductName,
								src: mainAsset?.image,
								originalWidth: width,
								originalHeight: height,
							},
						})
					}
				},
			}}
			assets={assets}
			name={name}
			{...props}
		/>
	)
}

TileBridge.defaultProps = {}
TileBridge.propTypes = { Tile: elementType, tileProps: object, variant: string }

export default TileBridge
